import { useCallback, useEffect, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentsWalletsService } from "../services/paymentsWallets.service";
import type { PayoutRecheckRun } from "../types/payments-wallets.types";

const POLL_MS = 3000;
/** A run asks at most a hundred providers; past this the page stops waiting and says so. */
const GIVE_UP_MS = 120_000;

export interface UsePayoutRecheckResult {
  run: PayoutRecheckRun | null;
  isRunning: boolean;
  /** Asks for a run; inside the backend's two-minute window this returns the run already queued. */
  start: () => void;
}

const settledAnything = (run: PayoutRecheckRun) =>
  !!run.result && run.result.settled + run.result.rejected > 0;

/**
 * Re-reads the payouts in progress at their providers when the staff overview opens: a payout only leaves "in
 * progress" on a provider webhook, and a lost one keeps the client's balance blocked. Starts a run, follows
 * it until it finishes, and calls `onSettled` when it moved any payout so the page re-reads its balances. A
 * denied request hides the whole thing — it is a convenience, never a gate.
 */
export function usePayoutRecheck(enabled: boolean, onSettled: () => void): UsePayoutRecheckResult {
  const [run, setRun] = useState<PayoutRecheckRun | null>(null);
  const [requestKey, setRequestKey] = useState(0);
  const onSettledRef = useRef(onSettled);
  useEffect(() => {
    onSettledRef.current = onSettled;
  }, [onSettled]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    const follow = (current: PayoutRecheckRun) => {
      if (cancelled) return;
      setRun(current);
      if (current.state === "completed") {
        if (settledAnything(current)) onSettledRef.current();
        return;
      }
      if (current.state === "failed" || current.state === "none") return;
      if (Date.now() - startedAt > GIVE_UP_MS) return;
      timer = setTimeout(() => {
        PaymentsWalletsService.payoutRecheckStatus()
          .then((res) => follow(res.data))
          .catch(() => undefined);
      }, POLL_MS);
    };

    PaymentsWalletsService.requestPayoutRecheck()
      .then((res) => follow(res.data))
      .catch((err: unknown) => {
        if (!cancelled && HttpApiService.isAccessDeniedError(err)) setRun(null);
      });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, requestKey]);

  const start = useCallback(() => setRequestKey((value) => value + 1), []);

  return {
    run,
    isRunning: run?.state === "waiting" || run?.state === "active",
    start,
  };
}
